import { useCallback, useState } from 'react';

export interface MediaPermissions {
  audio: boolean;
  video: boolean;
}

export type MediaType = 'audio' | 'video' | 'both';

/**
 * Hook for managing media permissions (camera and microphone).
 * Provides functions to request permissions and check current permission state.
 */
export function useMediaPermissions() {
  const [permissions, setPermissions] = useState<MediaPermissions | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isChecking, setIsChecking] = useState(false);

  const checkStreamPermissions = useCallback((stream: MediaStream): { audio: boolean; video: boolean } => {
    const allTracks = stream.getTracks() || [];
    const audio = allTracks.some((track) => track.kind === 'audio' && track.enabled === true);
    const video = allTracks.some((track) => track.kind === 'video' && track.enabled === true);

    allTracks.forEach((track) => {
      track.stop();
    });

    return { audio, video };
  }, []);

  const requestPermissions = useCallback(
    async (mediaType: MediaType = 'both'): Promise<MediaPermissions> => {
      setIsChecking(true);
      setError(null);

      try {
        const constraints = {
          audio: mediaType === 'audio' || mediaType === 'both',
          video: mediaType === 'video' || mediaType === 'both'
        };

        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        try {
          const { audio, video } = checkStreamPermissions(stream);
          const result = {
            audio: constraints.audio ? audio : false,
            video: constraints.video ? video : false
          };
          setPermissions(result);

          if ((constraints.audio && !result.audio) || (constraints.video && !result.video)) {
            setError('Permission denied for requested media');
          }

          setTimeout(() => navigator.mediaDevices.dispatchEvent(new Event('devicechange')), 500);

          setTimeout(() => {
            window.dispatchEvent(
              new CustomEvent('mediaPermissionsChanged', {
                detail: result
              })
            );
          }, 600);

          return result;
        } finally {
          stream.getTracks().forEach((track) => track.stop());
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        setError(errorMessage);
        throw err;
      } finally {
        setIsChecking(false);
      }
    },
    [checkStreamPermissions]
  );

  const checkCurrentPermissions = useCallback(async (): Promise<
    MediaPermissions & { audioBlocked?: boolean; videoBlocked?: boolean }
  > => {
    try {
      // Try to use Permissions API (supported in Chrome, Firefox, but not Safari)
      if (navigator.permissions) {
        const cameraPermission = await navigator.permissions.query({ name: 'camera' });
        const micPermission = await navigator.permissions.query({ name: 'microphone' });

        return {
          audio: micPermission.state === 'granted',
          video: cameraPermission.state === 'granted',
          // Determine if permissions are explicitly blocked (denied)
          audioBlocked: micPermission.state === 'denied',
          videoBlocked: cameraPermission.state === 'denied'
        };
      }
    } catch {
      console.log('Permissions API not available, falling back to enumerateDevices');
    }

    // Fallback for Safari and other browsers without Permissions API
    const devices = await navigator.mediaDevices.enumerateDevices();

    // In Safari, we can detect if permissions are granted by checking if device labels are available
    // Device labels are only exposed when permissions are granted
    const hasAudioPermission = !!devices.find((device) => device.kind === 'audioinput' && device.label);
    const hasVideoPermission = !!devices.find((device) => device.kind === 'videoinput' && device.label);

    // Note: Safari doesn't have a way to distinguish between "prompt" and "denied" states
    // We return undefined for blocked state, letting the component handle the UI accordingly
    return {
      audio: hasAudioPermission,
      video: hasVideoPermission,
      // Cannot determine if explicitly blocked in Safari - return undefined
      // Component will show button banner by default when permissions not granted
      audioBlocked: undefined,
      videoBlocked: undefined
    };
  }, []);

  return {
    permissions,
    error,
    isChecking,
    requestPermissions,
    checkCurrentPermissions
  };
}

export default useMediaPermissions;
